import { useAuth0 } from '@auth0/auth0-react';
import type { ReactNode, ComponentType } from 'react';
import Loading from 'src/modules/shared/components/atoms/loading';
import { useAuthBootstrap } from 'src/modules/shared/contexts/auth-bootstrap-context';

interface WithChildrenProps {
  children: ReactNode;
}

const withAuthentication = <P extends WithChildrenProps>(
  ProviderComponent: ComponentType<P>
) => {
  const WrappedComponent = (props: P) => {
    const { isAuthenticated, isLoading } = useAuth0();
    const { loading: bootstrapLoading, ready } = useAuthBootstrap();
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-screen w-full">
          <Loading size="lg" />
        </div>
      );
    }

    if (!isAuthenticated) {
      return <>{props.children}</>;
    }

    if (bootstrapLoading || !ready) {
      return (
        <div className="flex items-center justify-center h-screen w-full">
          <Loading size="lg" />
        </div>
      );
    }

    return <ProviderComponent {...props} />;
  };

  const componentName =
    [ProviderComponent.displayName, ProviderComponent.name, 'Component'].find(
      Boolean
    ) ?? 'Component';
  WrappedComponent.displayName = `withAuthentication(${componentName})`;

  return WrappedComponent;
};

export default withAuthentication;
